/**
 * mediaCodecDetector.js
 * Utility để tự động nhận diện Codec (H264, H265, VP8, VP9, AV1)
 * từ WebRTC RTCPeerConnection, HLS manifest (.m3u8), S3 Video URL (.mp4), hoặc HTMLMediaElement.
 */

/**
 * Chuẩn hóa tên Codec về định dạng thống nhất: H264 | H265 | VP8 | VP9 | AV1 | UNKNOWN
 * @param {string} rawStr - Chuỗi mimeType hoặc codec identifier (ví dụ: "video/H264", "avc1.640028", "hvc1.1.6.L93.B0")
 * @returns {"H264" | "H265" | "VP8" | "VP9" | "AV1" | "UNKNOWN"}
 */
export function normalizeCodecName(rawStr = '') {
  if (!rawStr || typeof rawStr !== 'string') return 'UNKNOWN';
  const str = rawStr.toLowerCase();

  if (
    str.includes('avc1') ||
    str.includes('avc3') ||
    str.includes('h264') ||
    str.includes('h.264')
  ) {
    return 'H264';
  }

  if (
    str.includes('hvc1') ||
    str.includes('hev1') ||
    str.includes('h265') ||
    str.includes('hevc') ||
    str.includes('h.265')
  ) {
    return 'H265';
  }

  if (str.includes('vp08') || str.includes('vp8')) return 'VP8';
  if (str.includes('vp09') || str.includes('vp9')) return 'VP9';
  if (str.includes('av01') || str.includes('av1')) return 'AV1';

  return 'UNKNOWN';
}

export async function detectWebRTCCodec(peerConnection) {
  console.log("🚀 ~ detectWebRTCCodec ~ peerConnection:", peerConnection)
  console.log("🚀 ~ detectWebRTCCodec ~ typeof peerConnection.getStats:", typeof peerConnection.getStats)
  if (!peerConnection || typeof peerConnection.getStats !== 'function') {
    return null;
  }

  try {
    const stats = await peerConnection.getStats();
    console.log("🚀 ~ detectWebRTCCodec ~ stats:", stats)
    let selectedCodecReport = null;
    let decoderImplementation = null;

    console.log("🚀 ~ detectWebRTCCodec ~ stats.values():", stats.values())
    for (const report of stats.values()) {
      console.log("🚀 ~ detectWebRTCCodec ~ report:", report)
      if (
        report.type === 'inbound-rtp' &&
        (report.kind === 'video' || report.mediaType === 'video')
      ) {
        decoderImplementation = report.decoderImplementation;
        if (report.codecId) {
          selectedCodecReport = stats.get(report.codecId);
          if (selectedCodecReport) break;
        }
      }
    }

    if (!selectedCodecReport) {
      for (const report of stats.values()) {
        if (
          report.type === 'codec' &&
          report.mimeType &&
          report.mimeType.toLowerCase().startsWith('video/')
        ) {
          selectedCodecReport = report;
          break;
        }
      }
    }

    if (selectedCodecReport && selectedCodecReport.mimeType) {
      const codecName = normalizeCodecName(selectedCodecReport.mimeType);
      return {
        sourceType: 'WebRTC',
        codec: codecName,
        mimeType: selectedCodecReport.mimeType,
        clockRate: selectedCodecReport.clockRate,
        decoder: decoderImplementation,
        sdpFmtpLine: selectedCodecReport.sdpFmtpLine,
      };
    }
  } catch (error) {
    console.warn('[mediaCodecDetector] detectWebRTCCodec error:', error);
  }

  return null;
}

const urlCodecCache = new Map();
const pendingFetches = new Map();

export async function detectHLSCodec(hlsOrUrl) {
  if (!hlsOrUrl) return null;

  if (typeof hlsOrUrl === 'object' && hlsOrUrl.levels) {
    const activeLevel =
      hlsOrUrl.levels[hlsOrUrl.currentLevel >= 0 ? hlsOrUrl.currentLevel : 0];
    const rawCodec = activeLevel?.videoCodec || activeLevel?.codecs || '';
    if (rawCodec) {
      return {
        sourceType: 'HLS',
        codec: normalizeCodecName(rawCodec),
        mimeType: rawCodec,
        resolution: activeLevel
          ? `${activeLevel.width}x${activeLevel.height}`
          : null,
      };
    }
  }

  if (typeof hlsOrUrl === 'string' && hlsOrUrl.includes('.m3u8')) {
    const cacheKey = hlsOrUrl.split('?')[0];
    if (urlCodecCache.has(cacheKey)) {
      return urlCodecCache.get(cacheKey);
    }
    if (pendingFetches.has(cacheKey)) {
      return await pendingFetches.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const res = await fetch(hlsOrUrl);
        const manifestText = await res.text();
        const match = manifestText.match(/CODECS="([^"]+)"/i);
        if (match && match[1]) {
          const codecStr = match[1];
          const result = {
            sourceType: 'HLS',
            codec: normalizeCodecName(codecStr),
            mimeType: codecStr,
          };
          urlCodecCache.set(cacheKey, result);
          return result;
        }
      } catch (e) {
      } finally {
        pendingFetches.delete(cacheKey);
      }
      return null;
    })();

    pendingFetches.set(cacheKey, fetchPromise);
    return await fetchPromise;
  }

  return null;
}

export async function detectMP4S3Codec(videoUrl) {
  console.log("🚀 ~ detectMP4S3Codec ~ videoUrl:", videoUrl)
  if (!videoUrl || typeof videoUrl !== 'string') return null;

  try {
    const res = await fetch(videoUrl, {
      headers: { Range: 'bytes=0-32767' },
    });
    console.log("🚀 ~ detectMP4S3Codec ~ res:", res)

    if (!res.ok && res.status !== 206) {
      return null;
    }

    const buffer = await res.arrayBuffer();
    console.log("🚀 ~ detectMP4S3Codec ~ buffer:", buffer)
    const bytes = new Uint8Array(buffer);

    let binaryString = '';
    for (let i = 0; i < bytes.length; i++) {
      binaryString += String.fromCharCode(bytes[i]);
    }

    // console.log("🚀 ~ detectMP4S3Codec ~ binaryString:", binaryString)
    if (binaryString.includes('hvc1') || binaryString.includes('hev1')) {
      return {
        sourceType: 'MP4_S3',
        codec: 'H265',
        mimeType: 'video/mp4; codecs="hvc1"',
      };
    }
    if (binaryString.includes('avc1') || binaryString.includes('avc3')) {
      return {
        sourceType: 'MP4_S3',
        codec: 'H264',
        mimeType: 'video/mp4; codecs="avc1"',
      };
    }
    if (binaryString.includes('vp09')) {
      return {
        sourceType: 'MP4_S3',
        codec: 'VP9',
        mimeType: 'video/mp4; codecs="vp09"',
      };
    }
    if (binaryString.includes('av01')) {
      return {
        sourceType: 'MP4_S3',
        codec: 'AV1',
        mimeType: 'video/mp4; codecs="av01"',
      };
    }
  } catch (error) {
    console.warn('[mediaCodecDetector] detectMP4S3Codec error:', error);
  }

  return null;
}

export async function detectMediaCodec(input) {
  console.log("🚀 ~ detectMediaCodec ~ input:", input)
  console.log("🚀 ~ detectMediaCodec ~ typeof input:", typeof input)
  if (!input) {
    return { codec: 'UNKNOWN', sourceType: 'UNKNOWN' };
  }

  if (typeof input === 'object') {
    const pc = input.peerConnection || input.refVideoStreamPc || (typeof input.getStats === 'function' ? input : null);
    if (pc && typeof pc.getStats === 'function') {
      const result = await detectWebRTCCodec(pc);
      if (result) return result;
    }

    const url = input.url || input.link || (typeof input.src === 'string' ? input.src : null);
    if (url && typeof url === 'string') {
      if (url.includes('.m3u8')) {
        const result = await detectHLSCodec(url);
        if (result) return result;
      } else {
        const result = await detectMP4S3Codec(url);
        if (result) return result;
      }
    }

    // 3. hls.js instance
    if (input.levels) {
      const result = await detectHLSCodec(input);
      if (result) return result;
    }
  }

  if (typeof input === 'string') {
    if (input.includes('.m3u8')) {
      const result = await detectHLSCodec(input);
      if (result) return result;
    } else if (
      input.match(/\.(mp4|mov|m4v)(\?.*)?$/i) ||
      input.includes('s3.') ||
      input.includes('beex.vn') ||
      input.startsWith('http')
    ) {
      const result = await detectMP4S3Codec(input);
      if (result) return result;
    }
  }

  return { codec: 'UNKNOWN', sourceType: 'UNKNOWN' };
}

/**
 * Kiểm tra xem trình duyệt hiện tại có hỗ trợ decode codec cụ thể hay không
 * @param {string} codecMimeType - ví dụ: 'video/mp4; codecs="avc1.640028"' hoặc 'video/mp4; codecs="hvc1.1.6.L93.B0"'
 * @param {number} width - default 1920
 * @param {number} height - default 1080
 * @param {number} framerate - default 30
 * @returns {Promise<{ supported: boolean, smooth: boolean, powerEfficient: boolean }>}
 */
export async function checkBrowserCodecSupport(
  codecMimeType,
  width = 1920,
  height = 1080,
  framerate = 30,
) {
  if (navigator.mediaCapabilities?.decodingInfo) {
    try {
      const result = await navigator.mediaCapabilities.decodingInfo({
        type: 'media-source',
        video: {
          contentType: codecMimeType,
          width,
          height,
          bitrate: 4000000,
          framerate,
        },
      });
      return {
        supported: !!result.supported,
        smooth: !!result.smooth,
        powerEfficient: !!result.powerEfficient,
      };
    } catch (e) {
      // ignore
    }
  }

  if (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported) {
    return {
      supported: MediaSource.isTypeSupported(codecMimeType),
      smooth: false,
      powerEfficient: false,
    };
  }

  return { supported: false, smooth: false, powerEfficient: false };
}
